import {createListenerMiddleware, isAnyOf} from '@reduxjs/toolkit';

// cat. redux actions
import {initWorkspace, setCurrentDepartment} from 'store/reducers/workspaceSlice';
import {getWorkspaceId} from 'config/workspace';
import {PERSISTED_PERSONAL} from 'config/constants';

/**
 * 워크스페이스 컨텍스트 영속화
 * - lastWorkspaceId: 마지막으로 활성화된 워크스페이스 (콜드 로드 시 복원)
 * - deptByWs: 워크스페이스별 마지막 선택 부서 (워크스페이스 단위로 스코프)
 *
 * 비핵심 UI 보조 상태이므로 localStorage 접근은 try/catch 로만 가드한다(프라이빗 모드/쿼터 예외 흡수).
 * 복원값은 소비 지점(App.js / BaseContainer)에서 서버 제공 목록으로 항상 재검증한다 — 저장값을 신뢰 소스로 쓰지 않는다.
 */
const STORAGE_KEY = 'into-seal:workspace-context';
const STORAGE_VERSION = 1;

const createEmptyState = () => ({
    version: STORAGE_VERSION,
    lastWorkspaceId: null,
    deptByWs: {},
});

const readState = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return createEmptyState();
        }

        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== STORAGE_VERSION) {
            return createEmptyState();
        }

        return {
            version: STORAGE_VERSION,
            lastWorkspaceId: parsed.lastWorkspaceId || null,
            deptByWs: (parsed.deptByWs && typeof parsed.deptByWs === 'object') ? parsed.deptByWs : {},
        };
    } catch (e) {
        return createEmptyState();
    }
};

const writeState = (next) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
        // 저장 실패는 무시 (UX 보조 기능)
    }
};

// ---- 복원 리더 (App.js, BaseContainer 보정 effect 용) ----
export const getPersistedWorkspaceId = () => readState().lastWorkspaceId;

export const getPersistedDepartmentId = (workspaceId) => {
    if (!workspaceId) {
        return null;
    }
    return readState().deptByWs[workspaceId] || null;
};

// ---- 저장 미들웨어 ----
export const persistWorkspaceMiddleware = createListenerMiddleware();

persistWorkspaceMiddleware.startListening({
    matcher: isAnyOf(initWorkspace, setCurrentDepartment),
    effect: (action, listenerApi) => {
        const state = readState();

        // 워크스페이스 진입/전환 → 마지막 워크스페이스 갱신
        if (initWorkspace.match(action)) {
            const workspaceId = getWorkspaceId(action.payload) || null;
            if (!workspaceId || workspaceId === state.lastWorkspaceId) {
                return;
            }
            writeState({...state, lastWorkspaceId: workspaceId});
            return;
        }

        // 부서 선택 변경 → 현재 워크스페이스 기준으로 저장
        if (setCurrentDepartment.match(action)) {
            const workspaceId = getWorkspaceId(listenerApi.getState().workspace) || null;
            if (!workspaceId) {
                return;
            }

            // null = '개인' 명시 선택 → sentinel 로 저장(미설정과 구분). 실제 부서 id 는 그대로 저장.
            const departmentId = action.payload || null;
            const nextDeptByWs = {...state.deptByWs};
            nextDeptByWs[workspaceId] = departmentId || PERSISTED_PERSONAL;

            writeState({...state, deptByWs: nextDeptByWs});
        }
    },
});
